import { Button } from "@solomon-apps/shared/src/components/ui/button";
import { Card } from "@solomon-apps/shared/src/components/ui/card";
import { Heading } from "@solomon-apps/shared/src/components/ui/heading";
import { Skeleton } from "@solomon-apps/shared/src/components/ui/skeleton";
import {
    TabsMenu,
    TabTrigger
} from "@solomon-apps/shared/src/components/ui/tabs-menu";
import { Text } from "@solomon-apps/shared/src/components/ui/text";
import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

import {
    useAllClientDistributions,
    useClientPositions
} from "@/hooks/useClientPositions";

import {
    hasChartHistory,
    isReportingRange,
    REPORTING_RANGES,
    type ReportingRange
} from "./Reporting/chartVisibility";
import { DistributionsInfiniteList } from "./Reporting/components/DistributionsInfiniteList";
import { ReportingState } from "./Reporting/components/ReportingState";
import { RewardsRateReport } from "./Reporting/components/RewardsRateReport";
import { StackedAreaReport } from "./Reporting/components/StackedAreaReport";
import { buildPositionColorMap, type GraphSlot } from "./Reporting/graphColors";
import {
    buildReportingDistributionSeries,
    type ReportingDistribution
} from "./Reporting/reportingModel";
import {
    deriveReportingPageState,
    type ReportingChartId
} from "./Reporting/reportingPageState";
import {
    buildCumulativeEarnings,
    buildSettlementReports,
    reportValues,
    SETTLEMENT_BALANCE_HINT
} from "./Reporting/reportingSeries";
import { ClientPage } from "./shared/ClientPage";

/**
 * YaaS Client Console — Reporting page.
 *
 * Routing scheme: single route `/reporting`, with an optional
 * `?view=distributions` query param that flips the page from the chart
 * **landing** to the Distributions **detail** ledger. Unknown or missing
 * `?view=` normalises back to landing without adding a history entry.
 *
 * Landing: three chart panels, in order, sharing one range control in the
 * page header:
 *   - Rewards Rate: realized APY from completed settlement periods.
 *   - Earnings (Cumulative): running payouts, stacked by position (from the
 *     distributions ledger, every page).
 *   - Average Qualifying Balance: settlement-period averages by holding.
 *
 * A panel renders only when its full history holds more than three values
 * (`chartVisibility.ts`). With none left, the body becomes the empty state;
 * a failed request becomes the error state (`reportingPageState.ts`).
 *
 * See `claude-docs/intents/yaas-client/reporting.md` for GIVEN/WHEN/THEN.
 */

type ReportingView = "landing" | "distributions";

const DISTRIBUTIONS_VIEW_PARAM = "distributions";

export function ReportingPage(): React.ReactElement {
    const distributionsQuery = useAllClientDistributions();
    const positionsQuery = useClientPositions(undefined, "all");
    const { positions } = positionsQuery;
    const [searchParams, setSearchParams] = useSearchParams();
    const [range, setRange] = useState<ReportingRange>("all");
    const [retrying, setRetrying] = useState(false);

    const rawView = searchParams.get("view");
    const activeView: ReportingView =
        rawView === DISTRIBUTIONS_VIEW_PARAM ? "distributions" : "landing";

    useEffect(() => {
        // Strip an unknown `view` param; `replace` keeps it out of history.
        if (rawView !== null && rawView !== DISTRIBUTIONS_VIEW_PARAM) {
            setSearchParams(
                (current) => {
                    const next = new URLSearchParams(current);
                    next.delete("view");
                    return next;
                },
                { replace: true }
            );
        }
    }, [rawView, setSearchParams]);

    const goToDistributionsDetail = (): void => {
        const next = new URLSearchParams(searchParams);
        next.set("view", DISTRIBUTIONS_VIEW_PARAM);
        setSearchParams(next);
    };

    const goToLanding = (): void => {
        const next = new URLSearchParams(searchParams);
        next.delete("view");
        setSearchParams(next);
    };

    const items = distributionsQuery.distributions;
    const { balances, rewardsRate } = useMemo(
        () => buildSettlementReports(positions),
        [positions]
    );
    const earnings = useMemo(
        () => buildCumulativeEarnings(items, positions),
        [items, positions]
    );

    const ledger = useMemo(
        () => buildReportingDistributionSeries(items, positions),
        [items, positions]
    );
    const ledgerColors = useMemo(
        () => buildPositionColorMap(ledger.positions),
        [ledger.positions]
    );

    const chartHasHistory: Record<ReportingChartId, boolean> = {
        rewardsRate: hasChartHistory(reportValues(rewardsRate)),
        earnings: hasChartHistory(reportValues(earnings.rows)),
        balances: hasChartHistory(reportValues(balances.rows))
    };

    const state = deriveReportingPageState({
        // Pending, not isLoading: a retry React Query pauses (hidden tab,
        // offline) reports isLoading false with no data and no error yet,
        // which would otherwise read as an empty history.
        isLoading:
            retrying ||
            distributionsQuery.isPending ||
            positionsQuery.isPending,
        hasError: Boolean(positionsQuery.error || distributionsQuery.error),
        chartHasHistory
    });

    const retry = (): void => {
        setRetrying(true);
        const pending: Promise<unknown>[] = [];
        if (positionsQuery.error) pending.push(positionsQuery.refetch());
        if (distributionsQuery.error)
            pending.push(distributionsQuery.refetch());
        void Promise.allSettled(pending).then(() => setRetrying(false));
    };

    const showRange =
        activeView === "landing" &&
        (state.kind === "charts" || state.kind === "loading");
    const rangeControl = !showRange ? undefined : state.kind === "loading" ? (
        <LoadingRangeTabs />
    ) : (
        <TabsMenu
            value={range}
            onValueChange={(value) => {
                if (typeof value === "string" && isReportingRange(value))
                    setRange(value);
            }}
            aria-label="Reporting range"
        >
            {REPORTING_RANGES.map((item) => (
                <TabTrigger key={item.value} value={item.value} size="xs">
                    {item.label}
                </TabTrigger>
            ))}
        </TabsMenu>
    );

    let body: React.ReactNode;
    if (state.kind === "loading") {
        body =
            activeView === "distributions" ? (
                <LoadingDistributionsDetail />
            ) : (
                <LoadingLanding />
            );
    } else if (state.kind === "error") {
        body = <ReportingState kind="error" onRetry={retry} />;
    } else if (activeView === "distributions") {
        body = (
            <DistributionsDetailView
                distributions={ledger.distributions}
                colorMap={ledgerColors}
                onBack={goToLanding}
            />
        );
    } else if (state.kind === "empty") {
        body = <ReportingState kind="empty" />;
    } else {
        const panels: Record<ReportingChartId, React.ReactNode> = {
            rewardsRate: <RewardsRateReport rows={rewardsRate} range={range} />,
            earnings: (
                <StackedAreaReport
                    title="Earnings (Cumulative)"
                    report={earnings}
                    range={range}
                    curve="stepAfter"
                    action={
                        <Button
                            variant="transparent"
                            size="xs"
                            interaction="underline"
                            onClick={goToDistributionsDetail}
                        >
                            See all distributions →
                        </Button>
                    }
                />
            ),
            balances: (
                <StackedAreaReport
                    title="Average Qualifying Balance"
                    hint={SETTLEMENT_BALANCE_HINT}
                    report={balances}
                    range={range}
                    curve="linear"
                />
            )
        };
        body = (
            <div className="flex flex-col gap-12">
                {state.visible.map((id) => (
                    <React.Fragment key={id}>{panels[id]}</React.Fragment>
                ))}
            </div>
        );
    }

    return (
        <ClientPage title="Reporting" actions={rangeControl}>
            <div className="px-6 py-6 min-w-0">{body}</div>
        </ClientPage>
    );
}

/* ── Distributions detail ────────────────────────────────── */

interface DistributionsDetailViewProps {
    distributions: ReportingDistribution[];
    colorMap: Record<string, GraphSlot>;
    onBack: () => void;
}

/**
 * Distributions detail view — the full ledger, list-first, so the "verify
 * every payout" use case is in the viewport without a chart above it.
 */
function DistributionsDetailView({
    distributions,
    colorMap,
    onBack
}: DistributionsDetailViewProps): React.ReactElement {
    return (
        <div className="flex flex-col gap-4">
            <div>
                <Button
                    variant="transparent"
                    size="xs"
                    interaction="underline"
                    onClick={onBack}
                >
                    ← Back to reporting
                </Button>
            </div>

            {distributions.length > 0 ? (
                <DistributionsInfiniteList
                    distributions={distributions}
                    colorMap={colorMap}
                />
            ) : (
                <DistributionsEmptyCard />
            )}
        </div>
    );
}

function DistributionsEmptyCard(): React.ReactElement {
    return (
        <Card
            asElement="section"
            border={false}
            rounded={false}
            aria-label="No distributions yet"
        >
            <Heading
                title="Distributions"
                titleVariant="h2"
                className="p-4 border-b border-line-base"
            />
            <div className="px-6 py-10 flex items-center justify-center">
                <Text
                    variant="body"
                    className="text-foreground-muted text-center max-w-sm"
                >
                    Your first distribution will appear here after the next
                    cycle. Each payout is verifiable on Solscan.
                </Text>
            </div>
        </Card>
    );
}

/* ── Loading placeholders ────────────────────────────────── */

function LoadingLanding(): React.ReactElement {
    return (
        <div className="flex flex-col gap-12" aria-busy="true">
            <LoadingReportCard titleWidth="w-36" legend={false} />
            <LoadingReportCard titleWidth="w-56" legend />
            <LoadingReportCard titleWidth="w-56" legend />
        </div>
    );
}

function LoadingReportCard({
    titleWidth,
    legend
}: {
    titleWidth: string;
    legend: boolean;
}): React.ReactElement {
    return (
        <section aria-label="Loading report">
            <div className="pb-3 border-b border-line-base">
                <Skeleton className={`h-7 ${titleWidth} rounded-none`} />
            </div>
            <div className="pt-4 pb-2">
                {/* Readout band + plot, as the loaded chart reserves them. */}
                <div className="h-68" />
            </div>
            {legend && (
                <div className="pt-3 flex items-center gap-x-5 gap-y-2 flex-wrap">
                    {Array.from({ length: 3 }).map((_, index) => (
                        <div
                            key={index}
                            className="inline-flex items-center gap-2"
                        >
                            <Skeleton className="size-2.5 rounded-none" />
                            <Skeleton className="h-4 w-24 rounded-none" />
                        </div>
                    ))}
                </div>
            )}
        </section>
    );
}

function LoadingRangeTabs(): React.ReactElement {
    return (
        <div className="flex items-center gap-2" aria-hidden>
            {Array.from({ length: 5 }).map((_, index) => (
                <Skeleton key={index} className="h-9 w-10 rounded-none" />
            ))}
        </div>
    );
}

function LoadingDistributionsDetail(): React.ReactElement {
    return (
        <div className="flex flex-col gap-4" aria-busy="true">
            <Skeleton className="h-8 w-36 rounded-none" />
            <Card
                asElement="section"
                border={false}
                rounded={false}
                aria-label="Loading all distributions"
            >
                <div className="flex items-baseline justify-between p-4 border-b border-line-base gap-4 flex-wrap">
                    <div className="flex flex-col gap-2">
                        <Skeleton className="h-4 w-32 rounded-none" />
                        <Skeleton className="h-7 w-72 rounded-none" />
                    </div>
                    <Skeleton className="h-4 w-32 rounded-none" />
                </div>

                <ul className="divide-y divide-line-base">
                    {Array.from({ length: 6 }).map((_, index) => (
                        <li key={index} className="px-4 py-4">
                            <div className="flex items-start justify-between gap-6 flex-wrap">
                                <div className="flex flex-col gap-2 min-w-36">
                                    <Skeleton className="h-6 w-32 rounded-none" />
                                    <Skeleton className="h-4 w-24 rounded-none" />
                                </div>
                                <Skeleton className="h-7 w-24 rounded-none" />
                            </div>
                            <div className="mt-3 flex flex-col gap-2">
                                {Array.from({ length: 2 }).map(
                                    (__, rowIndex) => (
                                        <div
                                            key={rowIndex}
                                            className="flex items-baseline justify-between gap-4 flex-wrap"
                                        >
                                            <div className="inline-flex items-center gap-2">
                                                <Skeleton className="h-3 w-3 rounded-none" />
                                                <Skeleton className="h-4 w-24 rounded-none" />
                                            </div>
                                            <div className="inline-flex items-center gap-4">
                                                <Skeleton className="h-5 w-20 rounded-none" />
                                                <Skeleton className="h-5 w-28 rounded-none" />
                                            </div>
                                        </div>
                                    )
                                )}
                            </div>
                        </li>
                    ))}
                </ul>

                <div className="px-4 py-6 flex items-center justify-center">
                    <Skeleton className="h-4 w-28 rounded-none" />
                </div>
            </Card>
        </div>
    );
}
